/**
 * UserImageTransformContext
 * 
 * Context per gestire gli stati temporanei delle trasformazioni utente su UserImage
 * utilizzando SharedValues per performance ottimali (60fps senza re-render).
 * 
 * Gestisce:
 * - Pan (trascinamento) temporaneo
 * - Zoom (scala) temporaneo  
 * - Rotation (rotazione) temporanea
 * 
 * Gli stati temporanei vengono applicati visivamente tramite useAnimatedStyle
 * e persistiti nel modello solo al termine del gesto.
 */
import { createContext, useContext } from 'react';
import { SharedValue } from 'react-native-reanimated';

/**
 * Stato temporaneo per le trasformazioni pan
 * null indica stato di quiete (nessuna trasformazione attiva)
 */
export interface TempPanState {
  x: number;
  y: number;
}

/**
 * Interface del UserImageTransformContext
 */
export interface UserImageTransformContextProps {
  // SharedValues per le trasformazioni temporanee (no re-render)
  // null indica stato di quiete (nessuna trasformazione temporanea attiva)
  tempPanSV: SharedValue<TempPanState | null>;
  tempZoomSV: SharedValue<number | null>;
  tempRotationSV: SharedValue<number | null>;
  
  // Reset degli stati temporanei
  resetTempState: () => void;
}

/**
 * Context con valori di default
 * Nota: Tutti i valori di default lanciano errori per garantire che il context
 * sia utilizzato solo all'interno del Provider appropriato
 */
export const UserImageTransformContext = createContext<UserImageTransformContextProps>({
  tempPanSV: { value: null } as SharedValue<TempPanState | null>,
  tempZoomSV: { value: null } as SharedValue<number | null>,
  tempRotationSV: { value: null } as SharedValue<number | null>,

  resetTempState: () => {
    throw new Error('resetTempState must be used within UserImageTransformProvider');
  },
});

/**
 * Hook per accedere al UserImageTransformContext
 * 
 * @returns Il context con tutti i valori e funzioni disponibili
 */
export const useUserImageTransformContext = () => {
  const context = useContext(UserImageTransformContext);
  
  if (!context) {
    throw new Error('useUserImageTransformContext must be used within UserImageTransformProvider');
  }
  
  return context;
};

/**
 * Utility hook per verificare se siamo dentro al UserImageTransformProvider
 * Non lancia errore, ma restituisce solo un boolean
 * 
 * @returns true se siamo dentro al provider, false altrimenti
 */
export const useIsInsideTransformProvider = (): boolean => {
  try {
    useUserImageTransformContext();
    return true;
  } catch {
    return false;
  }
};